import {
  Action,
  AssetUnitType,
  CDMEventData,
  DataCollectionEventNames,
  SimpleUiEvent,
  ValveControllerMetadata,
} from '@jarvis/jweb-core';
import { useCallback, useMemo } from 'react';
import { InputParameters, useInputParameters } from '../helpers/useInputParameters';
import { publishEvent } from '../jarvis/eventService';
import { getUserToken } from './auth';

const screenPath = '/PhotobookWebApp/';
const controlNameSuffix = 'Page';

export interface DataCollection {
  sendScreenViewEvent: (screenInfo: ScreenInfo, screenMode?: string) => Promise<void>;
  sendControlClickedEvent: (controlInfo: ControlInfo | string, screenInfo: ScreenInfo) => Promise<void>;
}

export type ScreenInfo = {
  name: string;
  activity: string;
};

export type ControlInfo = {
  name: string;
  detail?: string;
};

const simpleUiInfo = {
  gun: 'com.hp.cdm.domain.telemetry.type.eventDetail.category.simpleUi.version.1',
  version: '1.0.0',
  category: 'simpleUi',
};

const eventInfo = {
  version: '1.3.0',
};

const getValidAppInstanceId = (id?: string) => {
  if (id === undefined) {
    throw new Error(`Missing application instance ID`);
  }
  return id.toLowerCase();
};

const getValidCountry = (locale?: string) => {
  if (locale === undefined) {
    throw new Error(`Missing locale`);
  }
  const parts = locale.split('-');
  const country = parts[parts.length - 1].toLowerCase();
  if (country.length < 2 || country.length > 4) {
    throw new Error(`Invalid country code '${country}'`);
  }
  return country;
};

const getMetadata = (inputs: InputParameters): Promise<ValveControllerMetadata> => {
  return getUserToken().then((userToken) => {
    return {
      assetUnit: AssetUnitType.mobile,
      appInstanceId: getValidAppInstanceId(inputs.appInstanceId),
      accountLoginId: userToken.account.accountId,
      // TODO: Remove this 'country' parameter once native app defect JWEB-3445 is resolved
      country: getValidCountry(inputs.locale),
    };
  });
};

const sendSimpleUiEvent = async (event: SimpleUiEvent, inputs: InputParameters): Promise<void> => {
  console.log('DataCollection::sendSimpleUiEvent: %o', event);
  return getMetadata(inputs)
    .then((metadata) => {
      const date = new Date();
      const cdmEvent: CDMEventData = {
        dateTime: date.toISOString(),
        eventDetailType: simpleUiInfo.gun,
        eventCategory: simpleUiInfo.category,
        version: eventInfo.version,
        eventDetail: event,
      };
      const eventData = {
        events: [cdmEvent],
        valveControllerMetadata: metadata,
      };
      return publishEvent(DataCollectionEventNames.cdmEvent, eventData);
    })
    .catch((error) => console.error('DataCollection::sendSimpleUiEvent: %o', error));
};

export const useDataCollection = (): DataCollection => {
  const inputs = useInputParameters();

  const sendScreenViewEvent = useCallback(
    (screenInfo: ScreenInfo, screenMode?: string) => {
      const event: SimpleUiEvent = {
        version: simpleUiInfo.version,
        action: Action.screenDisplayed,
        activity: screenInfo.activity,
        screenPath: screenPath,
        screenName: screenInfo.name,
        screenMode: screenMode,
        controlName: inputs.experimentId ? `${screenInfo.name}${controlNameSuffix}` : undefined,
        controlDetail: inputs.experimentId,
      };
      return sendSimpleUiEvent(event, inputs);
    },
    [inputs],
  );

  const sendControlClickedEvent = useCallback(
    (controlInfo: ControlInfo | string, screenInfo: ScreenInfo) => {
      if (typeof controlInfo === 'string') {
        controlInfo = { name: controlInfo } as ControlInfo;
      }
      const event: SimpleUiEvent = {
        version: simpleUiInfo.version,
        action: Action.controlButtonClicked,
        activity: screenInfo.activity,
        screenPath: screenPath,
        screenName: screenInfo.name,
        controlName: controlInfo.name,
        controlDetail: controlInfo.detail,
      };
      return sendSimpleUiEvent(event, inputs);
    },
    [inputs],
  );

  const dataCollection: DataCollection = useMemo(
    () => ({
      sendScreenViewEvent: sendScreenViewEvent,
      sendControlClickedEvent: sendControlClickedEvent,
    }),
    [sendScreenViewEvent, sendControlClickedEvent],
  );

  return dataCollection;
};
